import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Trash2, Mail as MailIcon, Phone, MailOpen, Mail } from "lucide-react";
import NotifyEmailField from "@/components/NotifyEmailField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) => {
  try {
    return new Date(d.replace(" ", "T") + "Z").toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return d; }
};

export default function AdminContactTab() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contact/submissions`, { withCredentials: true });
      setSubmissions(data);
    } catch (err) {
      console.error("contact submissions fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleStatus = async (sub) => {
    const next = sub.status === "read" ? "new" : "read";
    try {
      await axios.put(`${API}/contact/submissions/${sub.id}`, { status: next }, { withCredentials: true });
      setSubmissions((arr) => arr.map((s) => (s.id === sub.id ? { ...s, status: next } : s)));
    } catch (err) {
      console.error(err);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/contact/submissions/${id}`, { withCredentials: true });
      setSubmissions((arr) => arr.filter((s) => s.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div data-testid="admin-contact-tab">
      <NotifyEmailField service="contact" label="Empfänger für Kontaktanfragen" />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl text-white flex items-center gap-2">
            <MailIcon className="w-5 h-5 text-[#C5993A]" /> Kontaktanfragen
          </h3>
          <p className="text-xs text-[#3A4F60] mt-1">{submissions.length} Eintr{submissions.length === 1 ? "ag" : "äge"} (max. 200)</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-16 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg">
          <p className="text-[#8899AA]">Noch keine Kontaktanfragen.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="contact-submissions-list">
          {submissions.map((s) => {
            const isNew = s.status !== "read";
            return (
              <div
                key={s.id}
                data-testid={`contact-${s.id}`}
                className={`bg-[#12202f] border rounded-lg p-4 ${isNew ? "border-[#C5993A]/40" : "border-[#3A4F60]/30"}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-medium truncate">{s.name}</p>
                      {isNew && <span className="text-[10px] uppercase bg-[#C5993A]/20 text-[#C5993A] px-1.5 py-0.5 rounded">Neu</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#8899AA]">
                      <span className="flex items-center gap-1"><MailIcon className="w-3 h-3" />{s.email}</span>
                      {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>}
                      <span className="text-[#3A4F60]">{fmt(s.created_at)}</span>
                    </div>
                    {s.message && <p className="text-sm text-[#F0F4F8] mt-2 whitespace-pre-line break-words">{s.message}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      data-testid={`toggle-status-${s.id}`}
                      onClick={() => toggleStatus(s)}
                      variant="ghost"
                      size="sm"
                      className="text-[#8899AA] hover:text-[#C5993A]"
                      title={isNew ? "Als gelesen markieren" : "Als neu markieren"}
                    >
                      {isNew ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                    </Button>
                    <Button
                      data-testid={`delete-contact-${s.id}`}
                      onClick={() => remove(s.id)}
                      variant="ghost"
                      size="sm"
                      className="text-[#8899AA] hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
